/**
 * Styled primitives for the DFC Fitness Village header.
 *
 * Every colour arrives through a `DfcSurface` (src/Utils/dfcSurface.ts) or `DFC_PALETTE`;
 * no literal is written here. Values are the design canvas's: 16px / 40px gutters, 30px /
 * 34px lockup, 32px / 38px pills, 2px green rule under the active nav item.
 */
import styled from '@emotion/styled';
import { AppBar, Badge, Box, ButtonBase } from '@mui/material';
import { DfcSurface } from '../../../../../Utils/dfcSurface';
import { DFC_PALETTE } from '../../../../../Utils/dfcBrand';
import { DFC_VILLAGE_DESKTOP_MIN_WIDTH } from '../../../../../hooks/useDfcVillageDesktop';

const omit = (...props: string[]) => ({
  shouldForwardProp: (prop: PropertyKey) => !props.includes(prop as string),
});

const desktop = `@media (min-width: ${DFC_VILLAGE_DESKTOP_MIN_WIDTH}px)`;

export interface SurfaceProp {
  surface: DfcSurface;
}

/** Focus ring the canvas never drew — every control here is keyboard reachable. */
const focusRing = (surface: DfcSurface) => `
  &:focus-visible {
    outline: 2px solid ${surface.dark ? DFC_PALETTE.green : DFC_PALETTE.black};
    outline-offset: 2px;
  }
`;

export const HeaderBar = styled(AppBar, omit('surface'))<SurfaceProp>`
  && {
    position: sticky;
    top: 0;
    width: 100%;
    padding: 10px 16px;
    box-sizing: border-box;
    box-shadow: none;
    background: ${({ surface }) => surface.headerBg};
    color: ${({ surface }) => surface.ink};
    backdrop-filter: blur(8px);
    border-bottom: 1px solid ${({ surface }) => surface.headerBorder};
    font-family: Inter, system-ui, sans-serif;

    ${desktop} {
      padding: 14px 40px;
    }
  }
`;

export const HeaderRow = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;

  ${desktop} {
    gap: 28px;
  }
`;

/**
 * The company logo (see VillageLogo); on the dark surface it sits on a white plate — unless
 * DFC's dark-background logo is showing (`bare`), which is drawn for black.
 */
export const LogoPlate = styled(ButtonBase, omit('surface', 'bare'))<
  SurfaceProp & { bare?: boolean }
>`
  && {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    border-radius: 8px;
    padding: ${({ surface, bare }) => (bare ? '0px' : surface.logoPad)};
    background: ${({ surface, bare }) => (bare ? 'transparent' : surface.logoBg)};
    ${({ surface }) => focusRing(surface)}

    /* The admin panel's logo can be any shape: height is the design's, width is capped so a wide
       mark never pushes the header actions off a 320px screen. */
    img {
      display: block;
      height: 30px;
      width: auto;
      max-width: 140px;
      object-fit: contain;

      ${desktop} {
        height: 34px;
        max-width: 220px;
      }
    }
  }
`;

export const NavList = styled(Box)`
  display: flex;
  align-items: center;
  gap: 26px;
  flex: 1 1 auto;
  min-width: 0;
`;

export const NavItem = styled(ButtonBase, omit('surface', 'active'))<
  SurfaceProp & { active: boolean }
>`
  && {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
    font-family: inherit;
    font-size: 13px;
    font-weight: ${({ active }) => (active ? 700 : 500)};
    color: ${({ active, surface }) => (active ? surface.ink : surface.ink2)};
    white-space: nowrap;
    ${({ surface }) => focusRing(surface)}
  }
`;

export const NavRule = styled('span', omit('active'))<{ active: boolean }>`
  display: block;
  height: 2px;
  border-radius: 999px;
  background: ${DFC_PALETTE.green};
  width: ${({ active }) => (active ? '18px' : '0px')};
  transition: width 0.2s ease;
`;

export const ActionGroup = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;

  ${desktop} {
    gap: 10px;
  }
`;

/** Action-row wrapper reused by the loading skeleton — same gaps as the real header. */
export const SkeletonActions = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;

  ${desktop} {
    gap: 10px;
  }
`;

export const PillButton = styled(ButtonBase, omit('surface', 'variant', 'size'))<
  SurfaceProp & { variant: 'solid' | 'outline'; size: 'sm' | 'md' }
>`
  && {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: ${({ size }) => (size === 'sm' ? '32px' : '38px')};
    padding: 0 ${({ size }) => (size === 'sm' ? '13px' : '18px')};
    border-radius: 999px;
    font-family: inherit;
    font-size: ${({ size }) => (size === 'sm' ? '12px' : '13px')};
    font-weight: 700;
    white-space: nowrap;
    background: ${({ surface, variant }) => (variant === 'solid' ? surface.ctaBg : 'transparent')};
    color: ${({ surface, variant }) => (variant === 'solid' ? surface.ctaFg : surface.ink)};
    border: ${({ surface, variant }) =>
      variant === 'solid' ? 'none' : `1px solid ${surface.border}`};
    ${({ surface }) => focusRing(surface)}
  }
`;

/** 34px round control on the track colour — theme toggle, account, cart. */
export const RoundButton = styled(ButtonBase, omit('surface'))<SurfaceProp>`
  && {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 999px;
    background: ${({ surface }) => surface.track};
    color: ${({ surface }) => surface.ink};
    ${({ surface }) => focusRing(surface)}

    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

/**
 * The language switch: a round control on mobile holding "ع" / "EN", a 34px pill on desktop
 * spelling the other language out. The label is written in that language, so it is set in the
 * village's Arabic face whichever page it sits on.
 */
export const LanguageButton = styled(ButtonBase, omit('surface'))<SurfaceProp>`
  && {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 34px;
    height: 34px;
    padding: 0 10px;
    border-radius: 999px;
    background: ${({ surface }) => surface.track};
    color: ${({ surface }) => surface.ink};
    font-family: inherit;
    font-size: 13px;
    font-weight: 700;
    line-height: 1;
    white-space: nowrap;
    ${({ surface }) => focusRing(surface)}

    ${desktop} {
      padding: 0 14px;
    }
  }

  &:lang(ar),
  & :lang(ar) {
    font-family: Dubai, system-ui, sans-serif;
    font-size: 14px;
  }
`;

/** The mobile hamburger: 38px rounded square holding three bars. */
export const MenuButton = styled(ButtonBase, omit('surface'))<SurfaceProp>`
  && {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    width: 38px;
    height: 38px;
    border-radius: 12px;
    background: ${({ surface }) => surface.track};
    ${({ surface }) => focusRing(surface)}
  }
`;

export const MenuBar = styled('span', omit('surface'))<SurfaceProp>`
  display: block;
  width: 17px;
  height: 2px;
  border-radius: 999px;
  background: ${({ surface }) => surface.ink};
`;

export const CartBadge = styled(Badge)`
  & .MuiBadge-badge {
    background: ${DFC_PALETTE.green};
    color: ${DFC_PALETTE.black};
    font-weight: 800;
    font-size: 10px;
    min-width: 16px;
    height: 16px;
    padding: 0 4px;
  }
`;

/* ---- Cart-hold countdown ---- */

/**
 * The thin strip that appears under the header while there is a live cart hold. Sits on the
 * accent surface so it reads as a signal without shouting; the same border as the header keeps
 * the two chrome bands visually joined.
 */
export const HoldStrip = styled(Box, omit('surface'))<SurfaceProp>`
  position: sticky;
  top: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 6px 16px;
  background: ${({ surface }) => surface.soft};
  border-bottom: 1px solid ${({ surface }) => surface.headerBorder};
  color: ${({ surface }) => surface.ink};
  font-size: 12px;
  font-weight: 600;

  ${desktop} {
    padding: 8px 40px;
  }
`;

export const HoldLabel = styled('span', omit('surface'))<SurfaceProp>`
  color: ${({ surface }) => surface.ink2};
`;

/** Tabular numerals so the timer does not jitter as digits change width. */
export const HoldTimer = styled('span', omit('surface'))<SurfaceProp>`
  font-variant-numeric: tabular-nums;
  font-weight: 800;
  color: ${({ surface }) => (surface.dark ? DFC_PALETTE.green : surface.ink)};
`;
